import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Footer from "components/layout/Footer";
import AccessibleIconButton from "components/base/AccessibleIconButton";
import RightDrawer from "components/base/RightDrawer";
import SnapshotBookmarkSidebar from "./SnapshotBookmarkSidebar";
import CollectionTableComponent from "features/SourceSearch/components/collection-table-component";
import { useColumns } from "features/SourceSearch/components/generate-columns";
import WorkCitedTable from "features/WorkCited/components/Table";
import CollectionNotes from "features/Collections/components/CollectionNotes";
import { fillParent, scrollingBox } from "utils/base-styles";
import { getRegularDate } from "utils/time.util";
import { getArchiveSnapshotDetail } from "../services";
import {
  mapSnapshotSources,
  mapSnapshotWorkCited,
  submissionFolderPath,
} from "../utils";

const TABS = [
  { key: "bookmarks", label: "Bookmarks" },
  { key: "workcited", label: "Works Cited" },
  { key: "collectionnotes", label: "Collection Notes" },
  { key: "teachernotes", label: "Teacher's Notes" },
];

// The collection grid's columns, minus the ones a snapshot has no data for
// (rating, score, reading level, cross-collection membership).
const SNAPSHOT_COLUMNS = [
  "title_chr",
  "sourceName_chr",
  "sourcePublishedDateTime_dtm",
  "isCitationAvailable_ysn",
  "notesCount_cnt",
];

const SNAPSHOT_VISIBILITY_MODEL = SNAPSHOT_COLUMNS.reduce(
  (model, field) => ({ ...model, [field]: true }),
  {}
);

/** One summary tile in the row above the tabs. */
function StatTile({ label, value, loading }) {
  const theme = useTheme();
  return (
    <Stack
      gap={1}
      alignItems="center"
      sx={{
        flex: 1,
        minWidth: 0,
        px: 2,
        py: 2.5,
        borderRadius: 1,
        backgroundColor: theme.palette.secondary.main,
      }}
    >
      <Typography
        variant="body2"
        color={theme.palette.secondary.contrastText}
        noWrap
        sx={{ maxWidth: "100%" }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="text" width={90} height={24} />
      ) : (
        // Long values (emails, distribution list names) wrap instead of spilling out
        // of the tile, which a centred noWrap value does on both sides.
        <Typography
          variant="body1"
          align="center"
          sx={{ maxWidth: "100%", overflowWrap: "anywhere" }}
        >
          {value}
        </Typography>
      )}
    </Stack>
  );
}

const noop = async () => {};

/**
 * A submission inside an archived folder. Archiving freezes the collection into a
 * snapshot, so nothing here is read from the live collection APIs — but it is laid out
 * like any other collection: the same Bookmarks / Works Cited / Notes tabs, read-only.
 */
export default function SubmissionSnapshotPage() {
  const { snapshotId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const apiRef = useGridApiRef();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedTab, setSelectedTab] = useState("bookmarks");
  const [selectedSource, setSelectedSource] = useState(null);
  const [sidebarOption, setSidebarOption] = useState("citation");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      setSnapshot(await getArchiveSnapshotDetail(Number(snapshotId)));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [snapshotId]);

  useEffect(() => { load(); }, [load]);

  const header = snapshot?.header || null;
  const bookmarkRows = useMemo(() => mapSnapshotSources(snapshot), [snapshot]);
  const workCitedRows = useMemo(() => mapSnapshotWorkCited(snapshot), [snapshot]);

  const folderId = header?.submissionFolderID_lng;

  const tabSx = (key) => ({
    fontStyle: "italic",
    cursor: "pointer",
    textDecoration: selectedTab === key ? "underline" : "none",
    textDecorationThickness: "2px",
    textUnderlineOffset: "8px",
    color:
      selectedTab === key ? theme.palette.primary.main : theme.palette.text.primary,
  });

  // The same grid, columns, and column-visibility controls a collection uses; only the
  // sidebar handler differs, because snapshot rows must not hit the live source APIs.
  const bookmarkColumns = useColumns({
    fields: SNAPSHOT_COLUMNS,
    persistedWidths: {},
    commonWidth: null,
    theme,
    handlers: {
      handleSidebarOpen: (row, option) => {
        setSidebarOption(
          ["notes", "citation", "readinglevel"].includes(option) ? option : "citation"
        );
        setSelectedSource(row);
      },
    },
  });

  const renderBookmarks = () => (
    <CollectionTableComponent
      apiRef={apiRef}
      rows={bookmarkRows}
      columns={bookmarkColumns}
      handleColumnResize={() => {}}
      visibilityModel={SNAPSHOT_VISIBILITY_MODEL}
      handleVisibilityChange={() => {}}
      getRowId={(row) => row.collectionSourceID_ids}
      onRowClick={({ row }) => {
        setSidebarOption("citation");
        setSelectedSource(row);
      }}
      showCustomEmptyMessage
      loading={loading}
      noRowsMessage="This snapshot contains no bookmarks."
    />
  );

  // The live table downloads the document from the server; a snapshot already carries
  // the frozen bibliography, so the same button saves that instead.
  const downloadWorkCited = (workCitedId) => {
    const entry = workCitedRows.find((row) => row.workCitedId_ids === workCitedId);
    if (!entry) return;
    const blob = new Blob(
      [`<html><body>${entry.citation_mem || ""}</body></html>`],
      { type: "text/html" }
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${entry.name_chr || "works-cited"}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderWorkCited = () => (
    <WorkCitedTable
      rows={workCitedRows}
      getWorkCitedDocument={downloadWorkCited}
      setWorkCitedName={() => {}}
      setWorkCitedFormatStyle={() => {}}
      setDeleteId={() => {}}
      setDeletePopup={() => {}}
      readOnly
      loading={loading}
    />
  );

  const renderTab = () => {
    switch (selectedTab) {
      case "workcited":
        return renderWorkCited();
      case "collectionnotes":
        return (
          <CollectionNotes
            markdownSummary={header?.collectionNote_chr || ""}
            setMarkdownSummary={() => {}}
            saveCollectionFolder={noop}
            handleCollectionOverview={noop}
            readOnly
            placeholder="No collection notes were saved with this submission."
          />
        );
      case "teachernotes":
        return (
          <CollectionNotes
            markdownSummary={header?.teacherNote_chr || ""}
            setMarkdownSummary={() => {}}
            saveCollectionFolder={noop}
            handleCollectionOverview={noop}
            readOnly
            placeholder="No teacher's notes were saved with this submission."
          />
        );
      default:
        return renderBookmarks();
    }
  };

  if (loadError && !snapshot) {
    return (
      <Box
        sx={{
          ...fillParent,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 1,
          p: 4,
        }}
      >
        <Typography variant="h6">Archived submission not found</Typography>
        <Typography variant="body1" color="text.secondary">
          This archived submission no longer exists, or you don't have access to it.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...fillParent, ...scrollingBox, px: { xs: 2, md: 4 } }}>
      <Helmet>
        <title>
          {`Sooth - Archived Submission${header?.collectionName_chr ? ` - ${header.collectionName_chr}` : ""}`}
        </title>
      </Helmet>
      <Stack gap={3} pb={8}>
        <Stack direction="row" gap={2} alignItems="center" sx={{ mt: 3, minWidth: 0 }}>
          <AccessibleIconButton
            title="Back to submission folder"
            onClick={() =>
              navigate(
                folderId
                  ? submissionFolderPath({
                      // A snapshot only exists for an archived folder.
                      submissionFolderID_ids: folderId,
                      isArchived_ysn: true,
                    })
                  : "/collections/submissions"
              )
            }
          >
            <ArrowBackIcon />
          </AccessibleIconButton>
          {loading && !header ? (
            <Skeleton variant="text" width="30rem" height={48} />
          ) : (
            <Typography
              color="#5c6d7e"
              variant="h3"
              fontSize="3rem"
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: { xs: "2.25rem", sm: "3.5rem" },
                maxHeight: "7rem",
                flexShrink: 1,
              }}
            >
              {header?.collectionName_chr || "Archived submission"}
            </Typography>
          )}
        </Stack>

        <Alert severity="info">
          This is a read-only snapshot taken when the folder was archived. It does not
          change if the student edits their collection.
        </Alert>

        <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
          <StatTile label="Owner" loading={loading} value={header?.studentEmail_chr || "—"} />
          <StatTile
            label="Submitted"
            loading={loading}
            value={getRegularDate(header?.submittedDateTime_dtm)}
          />
          <StatTile
            label="Archived"
            loading={loading}
            value={getRegularDate(header?.archivedDateTime_dtm)}
          />
          <StatTile
            label="Sources"
            loading={loading}
            value={header?.sourceCount_int ?? bookmarkRows.length}
          />
          <StatTile
            label="Reviewed"
            loading={loading}
            value={header?.isReviewed_ysn ? "Yes" : "No"}
          />
        </Stack>

        <Stack direction="row" gap={3} flexWrap="wrap" sx={{ mb: "8px" }}>
          {TABS.map((tab) => (
            <Typography
              key={tab.key}
              variant="h5"
              sx={tabSx(tab.key)}
              onClick={() => setSelectedTab(tab.key)}
            >
              {tab.label}
            </Typography>
          ))}
        </Stack>

        {renderTab()}
      </Stack>
      <Footer sx={{ px: 0 }} />

      {/* The bookmark sidebar of an archived collection: everything is visible, nothing
          can be edited, so there are no summary, citation-style, or note controls. */}
      <RightDrawer open={Boolean(selectedSource)} onClose={() => setSelectedSource(null)}>
        <SnapshotBookmarkSidebar
          source={selectedSource}
          option={sidebarOption}
          onClose={() => setSelectedSource(null)}
        />
      </RightDrawer>
    </Box>
  );
}
